/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { joiResolver } from '@hookform/resolvers/joi';
import mbpLogger from 'mbp-logger';
import { object, func } from 'prop-types';

// MUI
import { Button, CircularProgress, Grid } from '@material-ui/core';

// Component
import AddMailingAddressForm from '../../AddNewRecipient/AddMailingAddress/AddMailingAddressForm';

// Helpers
import { schemaForMailingAddress } from '../../Helpers/Schema';
import { getUserProfile } from '../../../../../../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import { getAccessTokenSafely } from '../../../../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { RecipientBookContext } from '../../Context/RecipientBookContext';
import { hasSuggestedAddress } from '../../Helpers/QasHasAddress';
import { contryShort, unmaskPhoneNumber } from '../../../Common/Helpers/Helpers';
import { getBrand } from '../../../../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';

// API
import { processAddAddressBookEntry } from '../../../../../../../../../apis/account-apis/RecipientBook-apis/processAddAddressBookEntry';
// import StyledButton from '../../../../../../../../components/EnterpriseDesignSystem/Button/StyledButton';

// Icons
import closeIcon from '../../Assets/filterCloseIcon.svg';
import { CommonStyles } from '../../../../../../style/commonStyles';

const AddNewMailingAddress = ({
    classes, setAddNewAddressClicked, recipient
}) => {
    const {
        setIsAddressEditing, isVerificationNeeded,
        setIsVerificationNeeded, setConfirmAddress, formatedSelectedAddress, searchResults, setSearchResults, setFormatedSelectedAddress,
        keepAddressClicked, setIsLoadingConfirmation, setKeepAddressClicked, setChooseAddressClicked, currentRecipient, setCurrentRecipient,
    } = useContext(RecipientBookContext);
    const methods = useForm({ mode: 'all', resolver: joiResolver(schemaForMailingAddress) });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const useCommonStyles = CommonStyles();
    const userProfile = useSelector(getUserProfile) || {};
    const { ContactId } = userProfile || '';
    const brandId = useSelector(getBrand).id;

    const addAddressDataToRecipient = (addressResponse) => {
        if (Object.keys(currentRecipient).length !== 0) {
            if (currentRecipient.addresses?.length) {
                if (addressResponse.defaultKey === '1') {
                    // Check if any address in the recipient's addresses array has the same rcpId as the Response Data
                    currentRecipient.addresses.map((address) => delete address.defaultKey);
                }
                setCurrentRecipient({ ...currentRecipient, addresses: [...currentRecipient.addresses, addressResponse] });
            } else {
                currentRecipient['addresses'] = [addressResponse];
                setCurrentRecipient(currentRecipient);
            }
        }
    }


    const processAddressBookEntryMutation = useMutation({
        mutationFn: processAddAddressBookEntry,
        onSuccess: (data) => {
            if (data && data.status === 200 && data.data !== undefined) {
                const { addressResponse } = data?.data
                addAddressDataToRecipient(addressResponse)
            }
            setLoading(false);
            setIsLoadingConfirmation(false);
            setConfirmAddress(false);
            setError('');
            setTimeout(() => methods.reset(), 500);
            setAddNewAddressClicked(false);
            setIsAddressEditing(false);
        },
        onError: (err) => {
            setError('Something went wrong. Please try again.');
            setLoading(false);
            setIsLoadingConfirmation(false);
            setConfirmAddress(false);
            mbpLogger.logError({
                function: 'processAddressBookEntryMutation',
                appName: process.env.npm_package_name,
                module: 'AddNewMailingAddress.js',
                message: `Error While Adding an Address for Contact ID: ${userProfile?.ContactId}`,
                err,
            });
        },
    });

    const addNewMailingAddressEntry = async (newAddressPayload) => {
        setLoading(true);
        const jwtToken = await getAccessTokenSafely();
        processAddressBookEntryMutation.mutate({
            ContactId,
            data: newAddressPayload,
            jwtToken,
        });
    };

    useEffect(() => {
        // clear QAS state on unmount
        return () => {
            setIsVerificationNeeded(false);
            setConfirmAddress(false);
            setSearchResults([])
            setFormatedSelectedAddress({});
            setKeepAddressClicked(false);
            setChooseAddressClicked(false);
        }
    }, []);

    useEffect(() => {
        if (keepAddressClicked && !isVerificationNeeded) {
            methods.handleSubmit(onSubmit)();
        }
    }, [keepAddressClicked, isVerificationNeeded]);


    const handleOnSubmit = (data) => {
        const unmaskedPhoneNumber = data.phoneNumber ? unmaskPhoneNumber(data.phoneNumber) : '';
        const addSingleAddressPayload = {
            ...data,
            rcpEmployerName: recipient?.rcpEmployerName || '',
            firstName: !recipient.rcpFirstName && recipient.rcpEmployerName ? recipient.rcpEmployerName : recipient?.rcpFirstName,
            lastName: !recipient.rcpLastName && recipient.rcpEmployerName ? recipient.rcpEmployerName : recipient?.rcpLastName,
            defaultKey: data?.defaultKey ? '1' : '0',
            rcpId: recipient?.rcpId, // rcpId is required to add address, do not remove this
            createdUser: "SSP",
            country: data.country ? contryShort[data.country] : '',
            phoneNumber: unmaskedPhoneNumber,
            brandId
        };
        addNewMailingAddressEntry(addSingleAddressPayload);
    }

    const onSubmit = (data) => {
        if (isVerificationNeeded) {
            const hasAddress = hasSuggestedAddress({ formatedSelectedAddress, methods, searchResults });
            if (hasAddress) {
                setIsVerificationNeeded(false);
                return handleOnSubmit(data);
            } else {
                return setConfirmAddress(true);
            }
        }
        handleOnSubmit(data);
    };


    const hasErrors = !!Object.keys(methods.formState.errors).length > 0;

    return (
        <div className={classes.addNewRecipientWrapper}>
            <div className="addMailingFormContainer">
                <Grid container item xs={12} direction="row" className={classes.marginTop24}>
                    <Grid item xs={10} className='newAddress'>Add a new address</Grid>
                    <Grid container item xs={2} style={{ justifyContent: 'end' }}>
                        <div className={classes.closeIcon} onClick={() => setAddNewAddressClicked(false)} onKeyDown={() => { }}><span><img src={closeIcon} alt="close icon" /></span></div>
                    </Grid>
                </Grid>
                <form onSubmit={methods.handleSubmit(onSubmit)} className="formStyle">
                    <AddMailingAddressForm methods={methods} classes={classes} isViewingEditForm />
                    {error && (
                        <Grid container justifyContent="center" alignItems="center">
                            <p className={useCommonStyles.errorContainer}>{error}</p>
                        </Grid>
                    )}
                    <Grid container xs={12} spacing={2} justifyContent='center'>
                        {/* <Grid item xs={6}>
                            <Button variant="outlined" color="primary" fullWidth className="addAddressBtn" onClick={() => setAddNewAddressClicked(false)}>Cancel</Button>
                        </Grid> */}
                        <Grid item xs={12} md={4} lg={4}>
                            <Button
                                fullWidth
                                variant='contained'
                                className={classes.commonBtnContained}
                                onClick={methods.handleSubmit(onSubmit)}
                                style={{ width: '100%', margin: '0px', height: '46px' }}
                                type="submit"
                                disabled={loading || !methods.formState.isDirty || hasErrors}
                            >{loading ? <CircularProgress /> : 'Save'}
                            </Button>
                            {
                                /* THIS COMPONENT IS SHARED BETWEEN BOTH MOBILE AND DESKTOP - SINCE EDS BUTTON ONLY WORKS FOR MOBILE FOR NOW, DISABLING THIS TO NOT BREAK DESKTOP */
                            }
                            {
                                /*
                                    This EDS button styles are being overriden by the button styles inside main.js
     
                                    We should figure out what is protecting other buttons from inheriting the styles inside main.js but this button
                                    is not protected. Disabling this button for now.
                                */
                            }
                            {/* <StyledButton
                                fullWidth
                                variant='primary'
                                handleOnClick={methods.handleSubmit(onSubmit)}
                                type="submit"
                                disabled={loading}
                                label={loading ? <CircularProgress /> : 'Save'}
                            /> */}
                        </Grid>
                    </Grid>
                </form>
            </div>
        </div >
    );
};

AddNewMailingAddress.propTypes = {
    setAddNewAddressClicked: func.isRequired,
    recipient: object.isRequired,
    classes: object.isRequired,
};

export default AddNewMailingAddress;
